<script setup lang="ts">
import { EmptyState } from '@taler/ui'
import { formatRatioPercent, type Locale, type Ratio, ratioFraction } from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { BreakdownRow } from '../internal/view-types.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ rows: readonly BreakdownRow[]; caption: string }>()
const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

function percentText(share: Ratio | undefined): string {
  if (share === undefined) return '—'
  return `${formatRatioPercent(share, { locale: currentLocale.value, minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
}

function barWidth(share: Ratio | undefined): string {
  if (share === undefined) return '0%'
  return `${Math.min(100, Math.max(0, ratioFraction(share) * 100))}%`
}
</script>

<template>
  <EmptyState
    v-if="rows.length === 0"
    :title="t('reporting.empty.title')"
    :body="t('reporting.empty.body')"
  />
  <div
    v-else
    class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <table class="w-full border-collapse text-left text-[length:var(--text-step-2)]">
      <caption class="sr-only">
        {{
          caption
        }}
      </caption>
      <thead class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        <tr>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] font-medium">
            {{ t('reporting.table.name') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.table.entries') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.table.amount') }}
          </th>
          <th
            scope="col"
            class="w-[28%] px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium"
          >
            {{ t('reporting.table.share') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key" class="border-t border-[color:var(--color-border)]">
          <th
            scope="row"
            class="px-[var(--space-group)] py-[var(--space-inline)] font-medium text-[color:var(--color-text)]"
          >
            {{ row.label }}
          </th>
          <td
            class="px-[var(--space-group)] py-[var(--space-inline)] text-right tabular-nums text-[color:var(--color-text-muted)]"
          >
            {{ row.entryCount }}
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.amount" size="figure" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)]">
            <div class="flex items-center justify-end gap-3">
              <span
                class="h-2 w-24 overflow-hidden rounded-full bg-[color:var(--color-surface-sunken)]"
                aria-hidden="true"
              >
                <span
                  class="block h-full rounded-full bg-[color:var(--color-text-muted)]"
                  :style="{ width: barWidth(row.share) }"
                />
              </span>
              <span class="w-12 text-right tabular-nums text-[color:var(--color-text-muted)]">{{
                percentText(row.share)
              }}</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
